import { ItemInfo } from "../models/item";
import {
	PartnerInvestmentData,
	PartnerInvestmentItemLink,
	PartnerInvestmentRecord
} from "../models/partnerInvestment";
import { getInvestmentCodeForItem } from "./partnerInvestmentLookup";

const hasInvestmentLinkForItem = (
	item: ItemInfo,
	itemKey: string,
	investments: PartnerInvestmentRecord[]
): boolean =>
	investments.some((investment) =>
		(investment.itemLinks || []).some(
			(link) =>
				link.itemCode === item.itemCode || link.itemKey === itemKey
		)
	);

export interface PartnerInvestmentDraft {
	fundingSource?: "partner" | "shopCash";
	partnerName?: string | null;
	amount: number | string;
	investmentDate: string;
	description?: string | null;
	itemLinks?: PartnerInvestmentItemLink[];
	shopCashRef?: string | null;
	createdAt?: string;
	userId?: string;
	username?: string;
	code?: string | null;
}

export const buildInvestmentDateValue = (date: string) =>
	`${date}T12:00:00.000Z`;

export const validatePartnerInvestmentDraft = (
	draft: PartnerInvestmentDraft
): string | null => {
	const parsedAmount = Number(draft.amount);
	if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
		return "Investment amount must be greater than zero.";
	}

	if (draft.fundingSource !== "shopCash") {
		if (!String(draft.partnerName || "").trim()) {
			return "Please enter partner name.";
		}
	}

	if (!String(draft.description || "").trim()) {
		return "Please enter description.";
	}

	if (draft.fundingSource === "shopCash" && !draft.shopCashRef) {
		return "Please select a linked Shop Cash expense.";
	}

	return null;
};

export const buildPartnerInvestmentPayload = (
	draft: PartnerInvestmentDraft
): PartnerInvestmentData => {
	const parsedAmount = Number(draft.amount);
	return {
		partnerName:
			draft.fundingSource === "shopCash"
				? null
				: String(draft.partnerName || "").trim() || null,
		amount: parsedAmount,
		investmentDate: buildInvestmentDateValue(draft.investmentDate),
		description: String(draft.description || "").trim(),
		status: "active",
		itemLinks: draft.itemLinks || [],
		fundingSource: draft.fundingSource,
		shopCashRef:
			draft.fundingSource === "shopCash"
				? draft.shopCashRef || null
				: null,
		createdAt: draft.createdAt || new Date().toISOString(),
		userId: draft.userId || "unknown",
		username: draft.username || "Unknown",
		code: draft.code?.trim() || undefined
	};
};

export const createInvestmentItemLinksForSelection = async ({
	selectedItemKeys,
	selectedItemsByKey,
	existingInvestments,
	resolveItemByKey
}: {
	selectedItemKeys: string[];
	selectedItemsByKey: Record<string, ItemInfo | undefined>;
	existingInvestments: PartnerInvestmentRecord[];
	resolveItemByKey: (itemKey: string) => Promise<ItemInfo | null | undefined>;
}): Promise<{
	itemLinks: PartnerInvestmentItemLink[];
	error: string | null;
}> => {
	const selectedEntries: PartnerInvestmentItemLink[] = [];

	for (const itemKey of selectedItemKeys) {
		const existingItem = selectedItemsByKey[itemKey];
		const item = existingItem ?? (await resolveItemByKey(itemKey));

		if (!item) {
			continue;
		}

		const hasExistingInvestment = hasInvestmentLinkForItem(
			item,
			itemKey,
			existingInvestments
		);

		if (hasExistingInvestment) {
			return {
				itemLinks: [],
				error: `Selected item ${item.itemCode} is already linked to an investment.`
			};
		}

		selectedEntries.push({
			itemKey,
			itemCode: item.itemCode,
			itemName: item.name,
			quantity: item.availableQuantity || 0,
			itemPurchasePrice: item.purchasePrice ?? 0
		});
	}

	if (!selectedEntries.length) {
		return {
			itemLinks: [],
			error: "Please select at least one valid item."
		};
	}

	return { itemLinks: selectedEntries, error: null };
};

export const buildInvestmentCodeMapForItems = (
	items: Record<string, ItemInfo>,
	investments: PartnerInvestmentRecord[]
): Record<string, string | null> =>
	Object.keys(items).reduce<Record<string, string | null>>((acc, key) => {
		acc[key] = getInvestmentCodeForItem(items[key], investments);
		return acc;
	}, {});
